/**
 * `startNfsRuntime` — boots up an inpax VM around an NFS IPO and
 * exposes a `runCabimain(jobName)` entry point. Mirrors ncsx's
 * `startNcsRuntime` shape, scoped down to what NFS read-only flows
 * need.
 *
 * Three external dependencies, all from published inpax packages:
 *   - @emdzej/inpax-parser     — IPO bytecode → typed Instruction[]
 *   - @emdzej/inpax-interpreter — VM + scheduler
 *   - @emdzej/inpax-providers   — null providers for UI/SPS/etc.
 *   - @emdzej/inpax-mock-provider — mock EDIABAS (no real ECU needed)
 *
 * No ncsx dependency. Once `@emdzej/ncsx-inpax-cabi-provider` lands
 * on npm, the system-function table here is replaced by the full
 * one ncsx exposes, but the runtime shape stays the same.
 */
import { parseIpo } from '@emdzej/inpax-parser';
import { VM } from '@emdzej/inpax-interpreter';
import { StackEntryFlags, ValueType } from '@emdzej/inpax-core';
import { NullUIProvider, NullSimulationProvider, NullPrintProvider, NullPemProvider, NullDtmProvider, NullExternalProvider, NullSpsProvider, } from '@emdzej/inpax-providers/null';
import { MockEdiabasProvider } from '@emdzej/inpax-mock-provider';
import { CabiState } from './state.js';
import { buildSystemFunctions } from './system-functions.js';
export async function startNfsRuntime(options) {
    // 1. Parse the IPO from caller-supplied bytes. Node consumers
    //    with a path use the `./node` subpath's convenience wrapper.
    const ipo = parseIpo(options.ipoBytes);
    // 2. CABI state. Pre-seed cabd-pars / system-data if asked.
    const state = new CabiState();
    for (const [k, v] of Object.entries(options.cabdPars ?? {}))
        state.cabdPars.set(k, v);
    for (const [k, v] of Object.entries(options.systemData ?? {}))
        state.systemData.set(k, v);
    // 3. EDIABAS provider — caller-supplied or fresh mock. Same
    //    instance is wired into both the inpax runtime (so the VM's
    //    own internal callers can see it) AND the syscall overrides
    //    (so our CDHapiJob handler dispatches through it).
    const ediabas = options.ediabas ?? new MockEdiabasProvider();
    // 4. System-function overrides — the NCSEXPER-style slot table.
    const systemFunctions = buildSystemFunctions(state, {
        ediabas,
        defaultSgbd: options.sgbd,
        workingDir: options.workingDir,
        firmwareSource: options.firmwareSource,
        fileBackend: options.fileBackend,
        maxBinaryRetries: options.maxBinaryRetries,
        retryBackoffMs: options.retryBackoffMs,
        retryableStatuses: options.retryableStatuses,
    });
    // 5. Build the VM. All UI / simulation / etc. providers are
    //    null — NFS IPOs we run for the hello-world path are
    //    batch-mode (Jobs / Ident / AifLesen / …); no screens,
    //    menus, or state-machines fire.
    const vm = new VM(ipo, {
        runtime: {
            ui: new NullUIProvider(),
            ediabas,
            simulation: new NullSimulationProvider(),
            print: new NullPrintProvider(),
            pem: new NullPemProvider(),
            dtm: new NullDtmProvider(),
            external: new NullExternalProvider(),
            sps: new NullSpsProvider(),
        },
        systemFunctions,
        debug: process.env.NFSX_VM_DEBUG === '1',
    });
    // 5. Pre-seed common globals NCSEXPER's MFC UI would set before
    //    invoking the IPO. Two known ones today:
    //
    //      global[11] = "Standard"  ← ProzessorTyp (per
    //                                  16ACC65.ipo's cabimain check)
    //
    //    These mappings come from cabimain disassembly observation;
    //    when other IPOs show different global checks we extend.
    const globals = vm.getGlobals();
    if (globals.length > 11) {
        globals[11] = {
            type: ValueType.String,
            flags: StackEntryFlags.ByValue,
            value: 'Standard',
        };
    }
    // We DON'T run `__inpa_startup__` — for NCSEXPER-style CABI
    // dispatchers it isn't required, and the null providers can't
    // service the interactive startup paths some NFS IPOs use. ncsx
    // runs it but catches errors; we skip outright since we've
    // seeded the globals startup would populate.
    const runCabimain = async (jobName) => {
        const cabimain = findFunction(vm, 'cabimain');
        if (!cabimain) {
            throw new Error(`cabimain not found in ${options.ipoPath} — this IPO isn't a CABI-style dispatcher`);
        }
        // Seed JOBNAME so cabimain's CDHGetCabdPar("JOBNAME") returns
        // the right value. Also push it onto the local stack as
        // local[0] (defensive — some IPOs read it from there too).
        state.cabdPars.set('JOBNAME', jobName);
        const ctx = vm.createExecutionContext();
        ctx.pushString(jobName);
        await vm.executeBlockWithContext(cabimain, ctx);
    };
    return { ipoPath: options.ipoPath, vm, state, ediabas, runCabimain };
}
function findFunction(vm, name) {
    for (const block of vm.getIpo().functions.values()) {
        if (block.header.name === name)
            return block;
    }
    return undefined;
}
//# sourceMappingURL=runtime.js.map